import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../../api/axiosInstance";
import { UserData } from "../../types/type";

export interface UpdateUserInput {
  username?: string;
  email?: string;
}

export const useGetUserInfo = () =>
  useQuery({
    queryKey: ["get-user-info"],
    queryFn: async () => {
      const { data } = await axios.get(`/api/profile/user-info`);
      return data as UserData;
    },
    retry: false, // optional: no auto-retries on 4xx
    staleTime: Infinity, // keep result — avoids refetch
  });

export const useUpdateUserInfo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData: UpdateUserInput) => {
      const { data } = await axios.put("/api/profile/user-info", userData);
      return data as UserData;
    },
    onSuccess: (data) => {
      // Optionally, update cached user info after successful mutation
      queryClient.setQueryData(["get-user-info"], data);
    },
  });
};

export const useVerifyNewEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({otp}: {otp: string}) => {
      const { data } = await axios.post("/api/profile/verify-new-email", {
        otp
      });
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["get-user-info"], (oldData: UserData | undefined) => {
        if (!oldData) return oldData;
        return { ...oldData, email: data.user.email };
      });
    },
    onError: (error: any) => {
      // Handle error
    },
  });
};


export const useChange2FAStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (is2FAEnabled: boolean) => {
      const { data } = await axios.put("/api/profile/2fa-toggle", {
        is_2fa_enabled: is2FAEnabled,
      });
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        ["get-user-info"],
        (oldData: UserData | undefined) => {
          if (!oldData) return oldData;
          return { ...oldData, is_2fa_enabled: data.user.is_2fa_enabled }; // Ensure this matches your API response!
        }
      );
    },
  });
};

export const useResetPassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      password: string;
      confirmPassword: string;
    }) => {
      const res = await axios.put("/api/profile/reset-password", data);
      return res.data;
    },
    onSuccess: () => {
      // Optionally invalidate queries or redirect after successful password reset
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await axios.delete("/api/profile/delete-user");
    },
    onSuccess: () => {
      // Invalidate user info query to remove user data
      queryClient.invalidateQueries({ queryKey: ["get-user-info"] });
    },
  });
};